import { supabase } from '@/config/supabase'
import { useAuthStore } from '@/stores/auth.store'
import type { Parceiro } from '@/types/database'
import type { ListFilters, PaginatedResult } from './associado.service'

function getTenantId(): string {
    const tid = useAuthStore.getState().tenant?.id
    if (!tid) throw new Error('Tenant não encontrado. Faça login novamente.')
    return tid
}

/** Gera API key aleatória no formato: pk_live_<40 hex chars> */
function generateApiKey(): string {
    const chars = 'abcdef0123456789'
    let key = 'pk_live_'
    for (let i = 0; i < 40; i++) {
        key += chars[Math.floor(Math.random() * chars.length)]
    }
    return key
}

export const parceiroService = {
    async list(filters: ListFilters = {}): Promise<PaginatedResult<Parceiro>> {
        const { search, page = 1, pageSize = 20 } = filters

        let query = supabase
            .schema('veicular_system')
            .from('veic_parceiros')
            .select('*', { count: 'exact' })
            .is('deleted_at', null)
            .order('nome', { ascending: true })

        if (search) {
            query = query.or(`nome.ilike.%${search}%,cnpj.ilike.%${search}%,wallet_id.ilike.%${search}%`)
        }
        const from = (page - 1) * pageSize
        query = query.range(from, from + pageSize - 1)

        const { data, count, error } = await query
        if (error) throw error
        return {
            data: (data || []) as Parceiro[],
            total: count || 0,
            page,
            pageSize,
            totalPages: Math.ceil((count || 0) / pageSize),
        }
    },

    async getAll(): Promise<Parceiro[]> {
        const { data, error } = await supabase
            .schema('veicular_system')
            .from('veic_parceiros')
            .select('*')
            .eq('ativo', true)
            .is('deleted_at', null)
            .order('nome')
        if (error) throw error
        return (data || []) as Parceiro[]
    },

    async getById(id: string): Promise<Parceiro | null> {
        const { data, error } = await supabase
            .schema('veicular_system')
            .from('veic_parceiros')
            .select('*')
            .eq('id', id)
            .is('deleted_at', null)
            .single()
        if (error) throw error
        return data as Parceiro
    },

    async create(input: Partial<Parceiro>): Promise<Parceiro> {
        const tenant_id = getTenantId()
        const api_key = generateApiKey()
        const { data, error } = await supabase
            .schema('veicular_system')
            .from('veic_parceiros')
            .insert({ ...input, tenant_id, api_key } as any)
            .select()
            .single()
        if (error) throw error
        return data as Parceiro
    },

    async update(id: string, input: Partial<Parceiro>): Promise<Parceiro> {
        // @ts-ignore
        const { data, error } = await supabase
            .schema('veicular_system')
            .from('veic_parceiros')
            .update(input as any)
            .eq('id', id)
            .select()
            .single()
        if (error) throw error
        return data as Parceiro
    },

    async regenerateApiKey(id: string): Promise<string> {
        const newKey = generateApiKey()
        // @ts-ignore
        const { error } = await supabase
            .schema('veicular_system')
            .from('veic_parceiros')
            .update({ api_key: newKey } as any)
            .eq('id', id)
        if (error) throw error
        return newKey
    },

    async remove(id: string): Promise<void> {
        // Soft delete
        // @ts-ignore
        const { error } = await supabase
            .schema('veicular_system')
            .from('veic_parceiros')
            .update({ ativo: false, deleted_at: new Date().toISOString() } as any)
            .eq('id', id)
        if (error) throw error
    },
}
